import { isMergedAudio, mergeAudio } from "./compat/audio-merge";
import { restoreVideoAudio } from "./compat/restore-video-audio";
import { useRef, useState } from "react";
import {
  useFullState,
  useCurrentStateAsRef,
  useWriteContext,
} from "../vendor/src/features/remotion-editor/editor/utils/use-context";
import { hasPendingTimelineEdit } from "./compat/edit-ownership";
import {
  transcriptionClip,
  detachVideoAudio,
  importSourceCaptions,
  addTranscribedCaptions,
} from "./compat/media-tools";
import { requestHost } from "./host/client";
import { editorUserMessage } from "../protocol/user-message";

export function MediaTools({
  disabled,
  save,
  revision,
}: {
  disabled: boolean;
  save: () => Promise<boolean>;
  revision: () => string | undefined;
}) {
  const state = useFullState(),
    current = useCurrentStateAsRef(),
    { setState } = useWriteContext();
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const running = useRef(false);
  const id = () => crypto.randomUUID();
  const blocked = disabled || busy || hasPendingTimelineEdit(state);
  async function run(
    kind: "detach" | "restore" | "merge" | "source" | "transcribe",
  ) {
    if (blocked || running.current) return;
    running.current = true;
    setBusy(true);
    setMessage("");
    try {
      const before = current.current.undoableState,
        selected = [...current.current.selectedItems];
      let next = before;
      let nextSelection: string[] | undefined;
      if (kind === "detach") next = detachVideoAudio(before, selected, id);
      else if (kind === "restore") next = restoreVideoAudio(before, selected);
      else if (kind === "merge") {
        const merged = mergeAudio(
          current.current,
          selected.filter(
            (id) =>
              before.items[id]?.type === "audio" || isMergedAudio(before, id),
          ),
          id,
        );
        next = merged.undoableState;
        nextSelection = merged.selectedItems;
      } else if (kind === "source")
        next = importSourceCaptions(
          before,
          await requestHost({ method: "GET", path: "/__waka/source-captions" }),
          id,
        );
      else {
        const clip = selected.length === 1 ? before.items[selected[0]!] : null;
        if (!clip || (clip.type !== "video" && clip.type !== "audio"))
          throw Error("영상이나 음성을 하나 선택해 주세요.");
        if (before.tracks.some((t) => t.locked && t.items.includes(clip.id)))
          throw Error("선택한 트랙의 잠금을 먼저 풀어 주세요.");
        transcriptionClip(before, clip.id);
        if (!(await save())) throw Error("먼저 현재 작업을 저장해 주세요.");
        const savedRevision = revision();
        if (!savedRevision) throw Error("저장 시점을 확인하지 못했습니다.");
        setMessage(
          "음성을 인식하고 있습니다. 원본 길이에 따라 잠시 걸릴 수 있습니다.",
        );
        const result = await requestHost({
          method: "POST",
          path: "/__waka/transcribe",
          body: { assetId: clip.assetId, revision: savedRevision },
        });
        next = addTranscribedCaptions(before, clip.id, result, id);
      }
      if (
        current.current.undoableState !== before ||
        hasPendingTimelineEdit(current.current)
      )
        throw Error(
          "편집 내용이 바뀌어 결과를 적용하지 않았습니다. 편집을 마친 뒤 다시 시도해 주세요.",
        );
      setState({
        commitToUndoStack: true,
        update: (prev) =>
          prev.undoableState === before
            ? {
                ...prev,
                undoableState: next,
                selectedItems:
                  nextSelection ??
                  prev.selectedItems.filter((id) => !!next.items[id]),
                assetStatus: {
                  ...prev.assetStatus,
                  ...Object.fromEntries(
                    Object.keys(next.assets)
                      .filter((key) => !before.assets[key])
                      .map((key) => [key, { type: "uploaded" as const }]),
                  ),
                },
              }
            : prev,
      });
      setMessage(
        kind === "detach"
          ? "오디오를 별도 트랙으로 분리했습니다. 되돌리기로 복원할 수 있습니다."
          : kind === "merge"
            ? "음성을 한 클립으로 병합했습니다. 우클릭에서 병합 해제하거나 되돌릴 수 있습니다."
            : kind === "restore"
              ? "영상 원음을 복구했습니다. 변경되지 않은 분리 음성은 정리했으며 되돌리기로 복원할 수 있습니다."
              : kind === "source"
                ? "앞단 자막을 원래 시간에 가져왔습니다. 컷을 바꿨다면 자막 위치를 확인해 주세요."
                : "자동자막을 추가했습니다. 내용을 확인한 뒤 저장해 주세요.",
      );
    } catch (error) {
      setMessage(
        editorUserMessage(
          error,
          "작업을 마치지 못했습니다. 다시 시도해 주세요.",
        ),
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <>
      <div
        className="precision-inline-tools"
        role="group"
        aria-label="음성·자막 도구"
      >
        <button
          type="button"
          disabled={blocked}
          title="선택한 영상의 소리를 별도 오디오 트랙으로 분리합니다."
          onClick={() => void run("detach")}
        >
          음성 분리
        </button>
        <button
          type="button"
          disabled={blocked}
          title="선택 영상의 원음을 켜고 변경하지 않은 분리 음성을 영상에 다시 결합합니다."
          onClick={() => void run("restore")}
        >
          영상 원음 복구
        </button>
        <button
          type="button"
          disabled={blocked}
          title="음성 클립 두 개 이상을 선택해 하나의 클립으로 편집합니다. 원본과 재생 타이밍은 유지합니다."
          onClick={() => void run("merge")}
        >
          음성 병합
        </button>
        <button
          type="button"
          disabled={blocked}
          onClick={() => void run("source")}
        >
          앞단 자막 가져오기
        </button>
        <button
          type="button"
          disabled={blocked}
          title="영상이나 음성 클립 하나를 선택하면 음성을 인식해 자막을 만듭니다."
          onClick={() => void run("transcribe")}
        >
          자동자막
        </button>
      </div>
      {message && (
        <p className="precision-workbar-message" role="status">
          {message}
        </p>
      )}
    </>
  );
}
